/**
 * SnoozeIndicator
 *
 * Small badge displayed on snoozed incasso cards.
 * Shows the BellSlash icon and the snooze-until date.
 * Hovering shows the full snooze reason via a tooltip.
 */

import { BellSlash } from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';

interface SnoozeIndicatorProps {
  snoozeUntil: string;
  snoozeReason?: string | null;
}

export const SnoozeIndicator = ({ snoozeUntil, snoozeReason }: SnoozeIndicatorProps) => {
  let formattedDate = snoozeUntil;

  try {
    formattedDate = format(parseISO(snoozeUntil), 'dd/MM', { locale: nl });
  } catch {
    // If parsing fails, fall back to the raw string — Sentry will capture context
    console.error('[SnoozeIndicator] Failed to parse snoozeUntil date:', snoozeUntil);
  }

  const badge = (
    <div className="flex items-center gap-1 bg-neutral-100 text-neutral-secondary-mid rounded px-2 py-0.5 text-xs w-fit">
      <BellSlash weight="duotone" size={12} className="shrink-0" />
      <span>Gesnoozed tot {formattedDate}</span>
    </div>
  );

  if (!snoozeReason) {
    return badge;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <p className="text-xs">{snoozeReason}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
